import { useState, useEffect } from "react";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import axiosInstance from "../../utils/axiosInstance";
import LoginImageModal from "../admin/components/LoginImageModal";
import Modal from "../../components/Modal";
import Loader from "../../components/Loader";
import { IoArrowBackSharp } from "react-icons/io5";

const SolutionImage = () => {
  const [images, setImages] = useState([]);
  const [error, setError] = useState();
  const [showAddImageModal, setShowAddImageModal] = useState(false);
  const [hoveredImage, setHoveredImage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await axiosInstance.get("/login/images");
        setImages(response.data);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, [reload]);

  const handleAddImageClick = () => {
    setShowAddImageModal(true);
  };

  const handleDeleteImageClick = (image) => {
    setImageToDelete(image);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(`/login/images/${imageToDelete.id}`);
      setImages(images.filter((img) => img.id !== imageToDelete.id));
      setShowDeleteModal(false);
    } catch (err) {
      setError("Failed to delete image.");
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-[1500px] space-y-5 px-4 pb-4 sm:px-6 sm:pb-6">
        <Loader />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1500px] space-y-5 px-4 pb-4 sm:px-6 sm:pb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="mb-1 flex items-center gap-3 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
          {/* Go Back Button */}
          <button
            onClick={() => window.history.back()} // This will navigate back to the previous page
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-500 transition hover:border-red-400/60 hover:text-red-500 dark:border-[#3a3a3d] dark:text-slate-400"
          >
            <IoArrowBackSharp size={24} />
          </button>
                <span className="h-6 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-red-500 to-orange-400" />
          Login Images
        </h3>

        <div className="flex space-x-2">
          <button
            onClick={handleAddImageClick}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 text-sm font-bold text-white transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Image
          </button>
        </div>
      </div>
      <div className="p-4 rounded-[22px] border border-gray-200 bg-white shadow-lg shadow-gray-900/5 dark:border-[#2c2c2e] dark:bg-[#1c1c1e] dark:shadow-black/40">
        <div className="grid grid-cols-5 gap-4">
          {images.map((image) => (
            <div
              key={image.id}
              className="relative cursor-pointer rounded-[16px] border border-gray-200 p-2 transition hover:border-red-400/60 dark:border-[#3a3a3d]"
              onMouseEnter={() => setHoveredImage(image.id)}
              onMouseLeave={() => setHoveredImage(null)}
            >
              {image.type === "IMAGE" ? (
                <img
                  src={image.url}
                  alt={image.name}
                  className="h-32 w-full object-cover rounded-md"
                />
              ) : (
                <video
                  width="600"
                  controls
                  preload="metadata"
                  style={{ border: "1px solid #ddd", borderRadius: "8px" }}
                >
                  <source src={image.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}

              {hoveredImage === image.id && (
                <div className="absolute top-2 right-2">
                  <button
                    onClick={() => handleDeleteImageClick(image)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-rose-500 text-white transition hover:bg-rose-400"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add Image Modal */}
        {showAddImageModal && (
          <LoginImageModal
            setShowAddImageModal={setShowAddImageModal}
            setReload={setReload}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <Modal
            title="Delete Image"
            content={`Are you sure you want to delete this image?`}
            onConfirm={handleConfirmDelete}
            onCancel={() => setShowDeleteModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default SolutionImage;
